import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Trophy, Medal, Star, TrendingUp, Users, Loader2, Award
} from 'lucide-react';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { useLanguage } from '../context/LanguageContext';
import { API_URL } from '../utils/apiConfig';

export default function ReferralLeaderboard({ token, isOpen, onClose }) {
  const [leaderboard, setLeaderboard] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all_time');
  const [programStats, setProgramStats] = useState(null);
  
  const { language } = useLanguage();
  
  const t = {
    en: {
      title: 'Referral Leaderboard',
      period: 'Period',
      allTime: 'All Time',
      thisMonth: 'This Month',
      thisWeek: 'This Week',
      rank: 'Rank',
      referrer: 'Referrer',
      referrals: 'Referrals',
      earned: 'Earned',
      yourRank: 'Your Rank',
      outOf: 'out of',
      referrers: 'referrers',
      topPercentile: 'Top',
      noRank: 'Not ranked yet',
      makeFirstReferral: 'Make your first referral to join!',
      totalReferrals: 'Total Program Referrals',
      totalCredits: 'Total Credits Awarded'
    },
    es: {
      title: 'Tabla de Clasificación',
      period: 'Período',
      allTime: 'Todo el tiempo',
      thisMonth: 'Este mes',
      thisWeek: 'Esta semana',
      rank: 'Posición',
      referrer: 'Referidor',
      referrals: 'Referidos',
      earned: 'Ganado',
      yourRank: 'Tu Posición',
      outOf: 'de',
      referrers: 'referidores',
      topPercentile: 'Top',
      noRank: 'Sin clasificar aún',
      makeFirstReferral: '¡Haz tu primera referencia para unirte!',
      totalReferrals: 'Total de Referidos del Programa',
      totalCredits: 'Total de Créditos Otorgados'
    }
  };
  
  const text = t[language] || t.en;
  
  useEffect(() => {
    if (isOpen) {
      fetchLeaderboard();
      if (token) {
        fetchMyRank();
      }
    }
  }, [isOpen, period, token]);
  
  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/referrals/leaderboard?period=${period}&limit=10`
      );
      if (response.ok) {
        const data = await response.json();
        setLeaderboard(data.leaderboard || []);
        setProgramStats({
          totalReferrals: data.total_program_referrals,
          totalCredits: data.total_program_credits
        });
      }
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchMyRank = async () => {
    try {
      const response = await fetch(`${API_URL}/api/referrals/my-rank`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMyRank(data);
      }
    } catch (error) {
      console.error('Failed to fetch my rank:', error);
    }
  };
  
  if (!isOpen) return null;
  
  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-5 h-5 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-slate-400" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="w-5 h-5 flex items-center justify-center text-sm font-bold text-slate-500">#{rank}</span>;
  };
  
  const getRankBg = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200';
    if (rank === 2) return 'bg-gradient-to-r from-slate-50 to-slate-100 border-slate-200';
    if (rank === 3) return 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200';
    return 'bg-white border-slate-100';
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-purple-600 to-indigo-600">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                <Trophy className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white">{text.title}</h2>
            </div>
            <Button variant="ghost" onClick={onClose} className="text-white hover:bg-white/20">×</Button>
          </div>
        </div>
        
        {/* Period Selector */}
        <div className="px-6 py-3 border-b border-slate-100 bg-slate-50">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">{text.period}</span>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-36" data-testid="leaderboard-period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all_time">{text.allTime}</SelectItem>
                <SelectItem value="month">{text.thisMonth}</SelectItem>
                <SelectItem value="week">{text.thisWeek}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* My Rank Card */}
              {token && myRank && (
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4 border border-purple-200 mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-purple-600 font-medium">{text.yourRank}</p>
                      {myRank.rank ? (
                        <>
                          <p className="text-2xl font-bold text-purple-900">
                            #{myRank.rank} <span className="text-sm font-normal text-purple-600">{text.outOf} {myRank.total_participants} {text.referrers}</span>
                          </p>
                          {myRank.percentile > 0 && (
                            <p className="text-sm text-purple-600">
                              {text.topPercentile} {myRank.percentile}%
                            </p>
                          )}
                        </>
                      ) : (
                        <>
                          <p className="text-lg font-bold text-purple-900">{text.noRank}</p>
                          <p className="text-sm text-purple-600">{text.makeFirstReferral}</p>
                        </>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-emerald-600">${myRank.credits_earned?.toFixed(2) || '0.00'}</p>
                      <p className="text-sm text-slate-500">{myRank.qualified_referrals || 0} {text.referrals}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Leaderboard */}
              <div className="space-y-2">
                {leaderboard.map((entry, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className={`flex items-center justify-between p-4 rounded-xl border ${getRankBg(entry.rank)}`}
                  >
                    <div className="flex items-center gap-3">
                      {getRankIcon(entry.rank)}
                      <div>
                        <p className="font-semibold text-slate-900">{entry.masked_name}</p>
                        {entry.badge && (
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-${entry.badge.color === 'gold' ? 'yellow' : entry.badge.color === 'silver' ? 'slate' : entry.badge.color === 'bronze' ? 'amber' : entry.badge.color}-100 text-${entry.badge.color === 'gold' ? 'yellow' : entry.badge.color === 'silver' ? 'slate' : entry.badge.color === 'bronze' ? 'amber' : entry.badge.color}-700`}>
                            {entry.badge.icon} {entry.badge.label}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-emerald-600">${entry.credits_earned.toFixed(2)}</p>
                      <p className="text-xs text-slate-500">{entry.referrals} {text.referrals}</p>
                    </div>
                  </motion.div>
                ))}
                
                {leaderboard.length === 0 && (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No referrals yet for this period</p>
                  </div>
                )}
              </div>
              
              {/* Program Stats */}
              {programStats && (
                <div className="mt-6 pt-4 border-t border-slate-200">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-slate-900">{programStats.totalReferrals}</p>
                      <p className="text-xs text-slate-500">{text.totalReferrals}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-emerald-600">${programStats.totalCredits?.toFixed(0)}</p>
                      <p className="text-xs text-slate-500">{text.totalCredits}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
